(() => {
  if (window.KenzuPageHeroBooted) return;

  window.KenzuPageHeroBooted = true;

  const HERO_SELECTOR = "[data-kenzu-page-hero]";
  const EXTERNAL_VIDEO_SELECTOR = "[data-external-video]";
  const SCROLL_SELECTOR = "[data-scroll-indicator]";

  const initializedHeroes = new WeakSet();
  const heroControllers = new WeakMap();

  class KenzuPageHero {
    constructor(hero) {
      this.hero = hero;
      this.externalVideo = hero.querySelector(
        EXTERNAL_VIDEO_SELECTOR
      );
      this.scrollButton = hero.querySelector(
        SCROLL_SELECTOR
      );
      this.hostedVideos = Array.from(
        hero.querySelectorAll("video")
      );

      this.autoplay =
        hero.dataset.autoplay === "true";

      this.pauseOutsideViewport =
        hero.dataset.pauseOutsideViewport === "true";

      this.prefersReducedMotion =
        window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        );

      this.abortController = new AbortController();
      this.visibilityObserver = null;
      this.videoObserver = null;

      this.handleScrollClick =
        this.handleScrollClick.bind(this);

      this.handleDocumentVisibility =
        this.handleDocumentVisibility.bind(this);

      this.initialize();
    }

    initialize() {
      const signal = this.abortController.signal;

      this.initializeEntranceAnimation();
      this.initializeExternalVideo();
      this.initializeHostedVideos();
      this.initializeVideoVisibility();

      if (this.scrollButton) {
        this.scrollButton.addEventListener(
          "click",
          this.handleScrollClick,
          { signal }
        );
      }

      document.addEventListener(
        "visibilitychange",
        this.handleDocumentVisibility,
        { signal }
      );
    }

    initializeEntranceAnimation() {
      if (
        this.prefersReducedMotion.matches ||
        !("IntersectionObserver" in window)
      ) {
        this.hero.classList.add("is-visible");
        return;
      }

      this.visibilityObserver =
        new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;

              this.hero.classList.add("is-visible");
              observer.unobserve(entry.target);
            });
          },
          {
            threshold: 0.12
          }
        );

      this.visibilityObserver.observe(this.hero);
    }

    initializeExternalVideo() {
      if (!this.externalVideo) return;

      const videoType =
        this.externalVideo.dataset.videoType;

      const videoId =
        this.externalVideo.dataset.videoId;

      if (!videoType || !videoId) return;

      const autoplay =
        this.externalVideo.dataset.autoplay === "true";

      const loop =
        this.externalVideo.dataset.loop === "true";

      const muted =
        this.externalVideo.dataset.muted === "true";

      const controls =
        this.externalVideo.dataset.controls === "true";

      const iframe = document.createElement("iframe");

      iframe.className =
        "kenzu-page-hero__external-iframe";

      iframe.allow =
        "autoplay; encrypted-media; picture-in-picture";

      iframe.setAttribute(
        "allowfullscreen",
        ""
      );

      iframe.setAttribute(
        "title",
        "Vídeo de presentación"
      );

      iframe.setAttribute(
        "loading",
        "eager"
      );

      if (videoType === "youtube") {
        const params = new URLSearchParams({
          autoplay: autoplay ? "1" : "0",
          mute: muted || autoplay ? "1" : "0",
          controls: controls ? "1" : "0",
          loop: loop ? "1" : "0",
          playlist: loop ? videoId : "",
          playsinline: "1",
          rel: "0",
          modestbranding: "1",
          enablejsapi: "1"
        });

        iframe.src =
          `https://www.youtube.com/embed/${encodeURIComponent(
            videoId
          )}?${params.toString()}`;
      }

      if (videoType === "vimeo") {
        const params = new URLSearchParams({
          autoplay: autoplay ? "1" : "0",
          muted: muted || autoplay ? "1" : "0",
          loop: loop ? "1" : "0",
          controls: controls ? "1" : "0",
          background:
            autoplay && !controls ? "1" : "0",
          autopause: "0"
        });

        iframe.src =
          `https://player.vimeo.com/video/${encodeURIComponent(
            videoId
          )}?${params.toString()}`;
      }

      if (!iframe.src) return;

      iframe.addEventListener(
        "load",
        () => {
          this.externalVideo.classList.add(
            "is-loaded"
          );
        },
        {
          once: true,
          signal: this.abortController.signal
        }
      );

      this.externalVideo.appendChild(iframe);
    }

    initializeHostedVideos() {
      if (!this.hostedVideos.length) return;

      this.hostedVideos.forEach((video) => {
        video.setAttribute("playsinline", "");

        if (this.autoplay) {
          video.muted = true;
          video.defaultMuted = true;

          const playPromise = video.play();

          if (
            playPromise &&
            typeof playPromise.catch === "function"
          ) {
            playPromise.catch(() => {
              /*
               * Algunos navegadores pueden bloquear autoplay.
               * El vídeo permanece disponible con su poster.
               */
            });
          }
        }
      });
    }

    initializeVideoVisibility() {
      if (!this.pauseOutsideViewport) return;
      if (!this.hostedVideos.length) return;
      if (!("IntersectionObserver" in window)) return;

      this.videoObserver =
        new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;

              if (!(video instanceof HTMLVideoElement)) {
                return;
              }

              if (
                entry.isIntersecting &&
                this.autoplay &&
                !document.hidden
              ) {
                const playPromise = video.play();

                if (
                  playPromise &&
                  typeof playPromise.catch === "function"
                ) {
                  playPromise.catch(() => {});
                }

                return;
              }

              video.pause();
            });
          },
          {
            threshold: 0.15
          }
        );

      this.hostedVideos.forEach((video) => {
        this.videoObserver.observe(video);
      });
    }

    handleDocumentVisibility() {
      if (!this.hostedVideos.length) return;

      if (document.hidden) {
        this.hostedVideos.forEach((video) => {
          video.pause();
        });

        return;
      }

      if (!this.autoplay) return;

      this.hostedVideos.forEach((video) => {
        const rectangle =
          video.getBoundingClientRect();

        const isVisible =
          rectangle.bottom > 0 &&
          rectangle.top < window.innerHeight;

        if (!isVisible) return;

        const playPromise = video.play();

        if (
          playPromise &&
          typeof playPromise.catch === "function"
        ) {
          playPromise.catch(() => {});
        }
      });
    }

    handleScrollClick() {
      if (!this.scrollButton) return;

      const targetSelector =
        this.scrollButton.dataset.scrollTarget;

      let target = null;

      if (
        targetSelector &&
        targetSelector !== "#MainContent"
      ) {
        try {
          target =
            document.querySelector(targetSelector);
        } catch (error) {
          target = null;
        }
      }

      if (!target || target === this.hero) {
        target = this.hero.nextElementSibling;
      }

      const reducedMotion =
        this.prefersReducedMotion.matches;

      if (target) {
        target.scrollIntoView({
          behavior: reducedMotion
            ? "auto"
            : "smooth",
          block: "start"
        });

        return;
      }

      const heroBottom =
        this.hero.getBoundingClientRect().bottom +
        window.scrollY;

      window.scrollTo({
        top: heroBottom,
        behavior: reducedMotion
          ? "auto"
          : "smooth"
      });
    }

    destroy() {
      this.abortController.abort();

      this.visibilityObserver?.disconnect();
      this.videoObserver?.disconnect();

      this.hostedVideos.forEach((video) => {
        video.pause();
      });
    }
  }

  const initializeHero = (hero) => {
    if (!(hero instanceof HTMLElement)) return;
    if (initializedHeroes.has(hero)) return;

    const controller = new KenzuPageHero(hero);

    initializedHeroes.add(hero);
    heroControllers.set(hero, controller);
  };

  const initializeHeroes = (root = document) => {
    if (
      root instanceof HTMLElement &&
      root.matches(HERO_SELECTOR)
    ) {
      initializeHero(root);
    }

    root
      .querySelectorAll?.(HERO_SELECTOR)
      .forEach(initializeHero);
  };

  const destroyHeroes = (root) => {
    const heroes = [];

    if (
      root instanceof HTMLElement &&
      root.matches(HERO_SELECTOR)
    ) {
      heroes.push(root);
    }

    root
      .querySelectorAll?.(HERO_SELECTOR)
      .forEach((hero) => {
        heroes.push(hero);
      });

    heroes.forEach((hero) => {
      heroControllers.get(hero)?.destroy();
      heroControllers.delete(hero);
    });
  };

  const boot = () => {
    initializeHeroes(document);
  };

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      boot,
      {
        once: true
      }
    );
  } else {
    boot();
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeHeroes(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      destroyHeroes(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:select",
    (event) => {
      const hero =
        event.target.matches?.(HERO_SELECTOR)
          ? event.target
          : event.target.querySelector?.(
              HERO_SELECTOR
            );

      if (!hero) return;

      hero.classList.add("is-visible");

      hero.querySelectorAll("video").forEach(
        (video) => {
          if (
            hero.dataset.autoplay === "true"
          ) {
            video.muted = true;

            const playPromise = video.play();

            if (
              playPromise &&
              typeof playPromise.catch ===
                "function"
            ) {
              playPromise.catch(() => {});
            }
          }
        }
      );
    }
  );
})();